import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  UserCheck, 
  MapPin, 
  Phone, 
  Mail, 
  Edit, 
  Plus,
  Calendar,
  ToggleLeft,
  ToggleRight,
  FileText,
  Award,
  Trash2,
  AlertCircle,
  Download,
  FolderOpen,
  Briefcase
} from 'lucide-react';
import { enqueteursData } from '../../data/mockData';

const EnqueteurDetail = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('informations');
  const [isEditing, setIsEditing] = useState(false);
  
  const enqueteur = enqueteursData.find(e => e.id === parseInt(id));

  if (!enqueteur) {
    return (
      <div className="text-center py-12">
        <UserCheck className="h-12 w-12 text-port-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-port-gray-900 mb-2">Enquêteur non trouvé</h3>
        <Link to="/admin/enqueteurs" className="btn btn-primary">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux enquêteurs
        </Link>
      </div>
    );
  }

  const toggleStatus = () => {
    // Ici on pourrait faire l'appel API pour changer le statut
    console.log('Toggle status for enqueteur', id);
  };

  // Vérifier les agréments expirés
  const agrementsExpires = enqueteur.aggrements.filter(agr => {
    const dateValidite = new Date(agr.dateValidite);
    return dateValidite < new Date();
  });

  const agrementsAExpirer = enqueteur.aggrements.filter(agr => {
    const dateValidite = new Date(agr.dateValidite);
    const aujourdhui = new Date();
    const dans30Jours = new Date(aujourdhui.getTime() + 30 * 24 * 60 * 60 * 1000);
    return dateValidite >= aujourdhui && dateValidite <= dans30Jours;
  });

  return (
    <div className="animate-slide-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Link 
            to="/admin/enqueteurs" 
            className="p-2 text-port-gray-600 hover:bg-port-gray-100 rounded-lg transition-colors mr-4"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-port-gray-900">
              {enqueteur.prenom} {enqueteur.nom}
            </h1>
            <p className="text-port-gray-600 flex items-center mt-1">
              <Briefcase className="h-4 w-4 mr-1" />
              {enqueteur.formeJuridique} • {enqueteur.numeroCertification}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Statut Toggle */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-port-gray-600">Compte:</span>
            <button
              onClick={toggleStatus}
              className={`flex items-center space-x-1 ${
                enqueteur.compteActif ? 'text-atl-green-600' : 'text-port-gray-400'
              }`}
            >
              {enqueteur.compteActif ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
              <span className="text-sm font-medium">
                {enqueteur.compteActif ? 'Actif' : 'Inactif'}
              </span>
            </button>
          </div>
          
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="btn btn-secondary"
          >
            <Edit className="h-4 w-4 mr-2" />
            {isEditing ? 'Annuler' : 'Modifier'}
          </button>
        </div>
      </div>

      {/* Alertes */}
      {(agrementsExpires.length > 0 || agrementsAExpirer.length > 0) && (
        <div className="mb-6 space-y-3">
          {agrementsExpires.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-900">Agrément(s) expiré(s)</h3>
                  <p className="text-sm text-red-700 mt-1">
                    {agrementsExpires.length} agrément{agrementsExpires.length > 1 ? 's ont' : ' a'} expiré{agrementsExpires.length > 1 ? 's' : ''}.
                    Veuillez les renouveler au plus vite.
                  </p>
                </div>
              </div>
            </div>
          )}
          {agrementsAExpirer.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-900">Agrément(s) à renouveler prochainement</h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    {agrementsAExpirer.length} agrément{agrementsAExpirer.length > 1 ? 's expirent' : ' expire'} dans les 30 prochains jours.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-port-gray-600">Dossiers traités</p>
                <p className="text-2xl font-bold text-port-gray-900">{enqueteur.nombreDossiers}</p>
              </div>
              <FolderOpen className="h-8 w-8 text-atl-blue-500" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-port-gray-600">Dossiers en cours</p>
                <p className="text-2xl font-bold text-atl-green-600">{enqueteur.dossiersEnCours}</p>
              </div>
              <FolderOpen className="h-8 w-8 text-atl-green-500" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-port-gray-600">Agréments</p>
                <p className="text-2xl font-bold text-atl-blue-600">{enqueteur.aggrements.length}</p>
              </div>
              <Award className="h-8 w-8 text-atl-blue-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div className="border-b border-port-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('informations')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'informations'
                ? 'border-atl-blue-500 text-atl-blue-600'
                : 'border-transparent text-port-gray-500 hover:text-port-gray-700 hover:border-port-gray-300'
            }`}
          >
            Informations
          </button>
          <button
            onClick={() => setActiveTab('aggrements')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'aggrements'
                ? 'border-atl-blue-500 text-atl-blue-600'
                : 'border-transparent text-port-gray-500 hover:text-port-gray-700 hover:border-port-gray-300'
            }`}
          >
            Agréments ({enqueteur.aggrements.length})
          </button>
        </nav>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'informations' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Informations personnelles */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium">Informations Personnelles</h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Prénom</label>
                    {isEditing ? (
                      <input type="text" defaultValue={enqueteur.prenom} className="form-input" />
                    ) : (
                      <p className="text-port-gray-900">{enqueteur.prenom}</p>
                    )}
                  </div>
                  <div>
                    <label className="form-label">Nom</label>
                    {isEditing ? (
                      <input type="text" defaultValue={enqueteur.nom} className="form-input" />
                    ) : (
                      <p className="text-port-gray-900">{enqueteur.nom}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="form-label flex items-center">
                    <Mail className="h-4 w-4 mr-1" />
                    Email
                  </label>
                  {isEditing ? (
                    <input type="email" defaultValue={enqueteur.email} className="form-input" />
                  ) : (
                    <p className="text-port-gray-900">{enqueteur.email}</p>
                  )}
                </div>

                <div>
                  <label className="form-label flex items-center">
                    <Phone className="h-4 w-4 mr-1" />
                    Téléphone
                  </label>
                  {isEditing ? (
                    <input type="tel" defaultValue={enqueteur.telephone} className="form-input" />
                  ) : (
                    <p className="text-port-gray-900">{enqueteur.telephone}</p>
                  )}
                </div>

                <div>
                  <label className="form-label flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    Adresse
                  </label>
                  {isEditing ? (
                    <textarea defaultValue={enqueteur.adresse} className="form-textarea" rows="2" />
                  ) : (
                    <p className="text-port-gray-900">{enqueteur.adresse}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Code Postal</label>
                    {isEditing ? (
                      <input type="text" defaultValue={enqueteur.codePostal} className="form-input" />
                    ) : (
                      <p className="text-port-gray-900">{enqueteur.codePostal}</p>
                    )}
                  </div>
                  <div>
                    <label className="form-label">Ville</label>
                    {isEditing ? (
                      <input type="text" defaultValue={enqueteur.ville} className="form-input" />
                    ) : (
                      <p className="text-port-gray-900">{enqueteur.ville}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Informations professionnelles */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium">Informations Professionnelles</h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                <div>
                  <label className="form-label">Forme Juridique</label>
                  {isEditing ? (
                    <select defaultValue={enqueteur.formeJuridique} className="form-select">
                      <option value="Auto-entrepreneur">Auto-entrepreneur</option>
                      <option value="EURL">EURL</option>
                      <option value="SASU">SASU</option>
                      <option value="SAS">SAS</option>
                      <option value="SARL">SARL</option>
                    </select>
                  ) : (
                    <p className="text-port-gray-900">{enqueteur.formeJuridique}</p>
                  )}
                </div>

                <div>
                  <label className="form-label flex items-center">
                    <Award className="h-4 w-4 mr-1" />
                    Numéro de Certification
                  </label>
                  {isEditing ? (
                    <input type="text" defaultValue={enqueteur.numeroCertification} className="form-input" />
                  ) : (
                    <p className="text-port-gray-900">{enqueteur.numeroCertification}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Compte Actif</label>
                  {isEditing ? (
                    <select defaultValue={enqueteur.compteActif} className="form-select">
                      <option value={true}>Oui</option>
                      <option value={false}>Non</option>
                    </select>
                  ) : (
                    <p className="text-port-gray-900">{enqueteur.compteActif ? 'Oui' : 'Non'}</p>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="mt-6 pt-4 border-t border-port-gray-200">
                  <div className="flex space-x-3">
                    <button className="btn btn-primary">
                      Enregistrer
                    </button>
                    <button 
                      onClick={() => setIsEditing(false)}
                      className="btn btn-secondary"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Références */}
          <div className="card lg:col-span-2">
            <div className="card-header">
              <h3 className="text-lg font-medium flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Références et Expériences
              </h3>
            </div>
            <div className="card-body">
              {isEditing ? (
                <textarea 
                  defaultValue={enqueteur.references} 
                  className="form-textarea" 
                  rows="6"
                  placeholder="Décrivez l'expérience et les références de l'enquêteur..."
                />
              ) : (
                <p className="text-port-gray-700 whitespace-pre-line">{enqueteur.references}</p>
              )}
            </div>
          </div>

          {/* Informations système */}
          <div className="card lg:col-span-2">
            <div className="card-header">
              <h3 className="text-lg font-medium">Informations Système</h3>
            </div>
            <div className="card-body">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <label className="form-label flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    Date de création
                  </label>
                  <p className="text-port-gray-900">{new Date(enqueteur.dateCreation).toLocaleDateString('fr-FR')}</p>
                </div>
                <div>
                  <label className="form-label">Dernière modification</label>
                  <p className="text-port-gray-900">{new Date(enqueteur.derniereModification).toLocaleDateString('fr-FR')}</p>
                </div>
                <div>
                  <label className="form-label">Nombre de dossiers traités</label>
                  <p className="text-2xl font-bold text-atl-blue-600">{enqueteur.nombreDossiers}</p>
                </div>
                <div>
                  <label className="form-label">Dossiers en cours</label>
                  <p className="text-2xl font-bold text-atl-green-600">{enqueteur.dossiersEnCours}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'aggrements' && (
        <div className="space-y-6">
          {/* Header avec bouton d'ajout */}
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Agréments et Certifications</h3>
            <button className="btn btn-primary btn-sm">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un agrément
            </button>
          </div>

          {/* Liste des agréments */}
          {enqueteur.aggrements.length === 0 ? (
            <div className="card">
              <div className="card-body text-center py-12">
                <Award className="h-12 w-12 text-port-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-port-gray-900 mb-2">
                  Aucun agrément enregistré
                </h3>
                <p className="text-port-gray-500 mb-4">
                  Ajoutez les agréments et certifications de cet enquêteur.
                </p>
                <button className="btn btn-primary btn-sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter le premier agrément
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {enqueteur.aggrements.map(agrement => {
                const dateValidite = new Date(agrement.dateValidite);
                const aujourdhui = new Date();
                const estExpire = dateValidite < aujourdhui;
                const dans30Jours = new Date(aujourdhui.getTime() + 30 * 24 * 60 * 60 * 1000);
                const expireBientot = !estExpire && dateValidite <= dans30Jours;

                return (
                  <div key={agrement.id} className={`card ${estExpire ? 'border-red-300' : expireBientot ? 'border-yellow-300' : ''}`}>
                    <div className="card-body">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-start flex-1">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                            estExpire ? 'bg-red-100' : expireBientot ? 'bg-yellow-100' : 'bg-atl-blue-100'
                          }`}>
                            <Award className={`h-5 w-5 ${
                              estExpire ? 'text-red-600' : expireBientot ? 'text-yellow-600' : 'text-atl-blue-600'
                            }`} />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-port-gray-900">{agrement.nom}</h4>
                            <p className="text-sm text-port-gray-500 mt-1">
                              {agrement.typeFichier} • {agrement.taille}
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-1 ml-2">
                          <button className="p-1 text-atl-blue-600 hover:text-atl-blue-800" title="Télécharger">
                            <Download className="h-4 w-4" />
                          </button>
                          <button className="p-1 text-port-gray-400 hover:text-port-gray-600" title="Modifier">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button className="p-1 text-red-400 hover:text-red-600" title="Supprimer">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-port-gray-600">Date d'ajout :</span>
                          <span className="text-port-gray-900">
                            {new Date(agrement.dateAjout).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-port-gray-600">Date de validité :</span>
                          <span className={`font-medium ${
                            estExpire ? 'text-red-600' : expireBientot ? 'text-yellow-600' : 'text-atl-green-600'
                          }`}>
                            {dateValidite.toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        {estExpire && (
                          <div className="mt-3 pt-3 border-t border-red-200">
                            <div className="flex items-center text-red-600">
                              <AlertCircle className="h-4 w-4 mr-1" />
                              <span className="text-xs font-medium">Agrément expiré</span>
                            </div>
                          </div>
                        )}
                        {expireBientot && (
                          <div className="mt-3 pt-3 border-t border-yellow-200">
                            <div className="flex items-center text-yellow-600">
                              <AlertCircle className="h-4 w-4 mr-1" />
                              <span className="text-xs font-medium">Expire prochainement</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EnqueteurDetail;

